/**
 * ASCII glitch ripple on hover for text links.
 * Adapted from https://codepen.io/erevan/pen/MYKBjdZ – faster timing.
 */

const WAVE_THRESH = 3;
const CHAR_MULT = 3;
const ANIM_STEP = 32;
const WAVE_BUF = 5;

const GLITCH_CHARS = '.,·-~+:;=*░▒▓█▄▀■!?&#$@0123456789';

function createGlitch(el, opts = {}) {
  let origTxt = el.textContent;
  let origChars = origTxt.split('');
  let isAnim = false;
  let cursorPos = 0;
  let waves = [];
  let animId = null;
  let origW = null;

  const cfg = {
    dur: 800,
    chars: GLITCH_CHARS,
    preserveSpaces: true,
    spread: 0.6,
    ...opts,
  };

  function updateCursorPos(e) {
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const len = origTxt.length;
    const pos = Math.round((x / rect.width) * len);
    cursorPos = Math.max(0, Math.min(pos, len - 1));
  }

  function startWave() {
    waves.push({ startPos: cursorPos, startTime: Date.now() });
    if (!isAnim) start();
  }

  function cleanupWaves(t) {
    waves = waves.filter((w) => t - w.startTime < cfg.dur);
  }

  function calcWaveEffect(charIdx, t) {
    let shouldAnim = false;
    let resultChar = origChars[charIdx];
    for (const w of waves) {
      const age = t - w.startTime;
      const prog = Math.min(age / cfg.dur, 1);
      const dist = Math.abs(charIdx - w.startPos);
      const maxDist = Math.max(w.startPos, origChars.length - w.startPos - 1);
      const rad = (prog * (maxDist + WAVE_BUF)) / cfg.spread;
      if (dist <= rad) {
        shouldAnim = true;
        const intens = Math.max(0, rad - dist);
        if (intens <= WAVE_THRESH && intens > 0) {
          const idx =
            (dist * CHAR_MULT + Math.floor(age / ANIM_STEP)) % cfg.chars.length;
          resultChar = cfg.chars[idx];
        }
      }
    }
    return { shouldAnim, char: resultChar };
  }

  function genScrambledTxt(t) {
    return origChars
      .map((char, i) => {
        if (cfg.preserveSpaces && char === ' ') return ' ';
        const res = calcWaveEffect(i, t);
        return res.shouldAnim ? res.char : char;
      })
      .join('');
  }

  function syncLabel() {
    if (el.dataset.glitchLabel !== undefined) {
      origTxt = el.dataset.glitchLabel;
      origChars = origTxt.split('');
    }
  }

  function stop() {
    syncLabel();
    el.textContent = origTxt;
    el.classList.remove('link-glitch-active');
    if (origW !== null) {
      el.style.width = '';
      origW = null;
    }
    isAnim = false;
  }

  function start() {
    syncLabel();
    if (isAnim) return;
    if (origW === null) {
      origW = el.getBoundingClientRect().width;
      el.style.width = `${origW}px`;
    }
    isAnim = true;
    el.classList.add('link-glitch-active');
    function animate() {
      const t = Date.now();
      cleanupWaves(t);
      if (waves.length === 0) {
        stop();
        return;
      }
      el.textContent = genScrambledTxt(t);
      animId = requestAnimationFrame(animate);
    }
    animId = requestAnimationFrame(animate);
  }

  const NAV_GLITCH_DELAY_MS = 350;

  function handleEnter(e) {
    const popover = el.closest('.nav-popover');
    if (popover && popover.dataset.openedAt) {
      const elapsed = Date.now() - parseInt(popover.dataset.openedAt, 10);
      if (elapsed < NAV_GLITCH_DELAY_MS) return;
    }
    updateCursorPos(e);
    startWave();
  }

  function handleMove(e) {
    const popover = el.closest('.nav-popover');
    if (popover && popover.dataset.openedAt) {
      const elapsed = Date.now() - parseInt(popover.dataset.openedAt, 10);
      if (elapsed < NAV_GLITCH_DELAY_MS) return;
    }
    const old = cursorPos;
    updateCursorPos(e);
    if (cursorPos !== old) startWave();
  }

  el.addEventListener('mouseenter', handleEnter);
  el.addEventListener('mousemove', handleMove);

  // Expose cancel so external code can immediately stop the animation
  el._glitchCancel = () => {
    waves = [];
    if (animId !== null) {
      cancelAnimationFrame(animId);
      animId = null;
    }
    stop();
  };
}

/**
 * Attach glitch effect to text links (main content and nav popover).
 * Nav links only trigger after the menu has been open for a short delay. doesn’t * Original: https://codepen.io/erevan/pen/MYKBjdZ
 */
export function setupLinkGlitch() {
  const links = document.querySelectorAll(
    'main p a, .nav-popover-links .nav-link, .site-footer-link',
  );
  links.forEach((link) => {
    if (!link.textContent.trim()) return;
    createGlitch(link);
  });
}
